import { Button as ShadcnButton } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Loader2, UserPlus } from 'lucide-react'
import { Controller, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Checkbox } from 'vnetwork-ui'
import type { CreateAccountForm } from './types'

interface CreateAccountDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    form: UseFormReturn<CreateAccountForm>
    contactEmail: string
    onSubmit: (formData: CreateAccountForm) => Promise<void>
    isCreating: boolean
}

const CreateAccountDialog: React.FC<CreateAccountDialogProps> = ({
    open,
    onOpenChange,
    form,
    contactEmail,
    onSubmit,
    isCreating,
}) => {
    const { t } = useTranslation()
    const { t: tCustomer } = useTranslation('customer')

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <UserPlus className="h-5 w-5" />
                        {tCustomer('contact.create-keycloak-account')}
                    </DialogTitle>
                    <DialogDescription>{contactEmail}</DialogDescription>
                </DialogHeader>
                <form
                    onSubmit={form.handleSubmit((formData) =>
                        onSubmit(formData)
                    )}
                    className="space-y-4 mt-4"
                >
                    <div className="space-y-2">
                        <Label htmlFor="password">{t('password')}</Label>
                        <Input
                            id="password"
                            type="password"
                            {...form.register('password')}
                            placeholder="••••••••"
                            className={
                                form.formState.errors.password
                                    ? 'border-red-300'
                                    : ''
                            }
                        />
                        {form.formState.errors.password && (
                            <p className="text-xs text-red-500">
                                {form.formState.errors.password.message}
                            </p>
                        )}
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="password_confirmation">
                            {tCustomer('contact.confirm-password')}
                        </Label>
                        <Input
                            id="password_confirmation"
                            type="password"
                            {...form.register('password_confirmation')}
                            placeholder="••••••••"
                            className={
                                form.formState.errors.password_confirmation
                                    ? 'border-red-300'
                                    : ''
                            }
                        />
                        {form.formState.errors.password_confirmation && (
                            <p className="text-xs text-red-500">
                                {
                                    form.formState.errors.password_confirmation
                                        .message
                                }
                            </p>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <Controller
                            name="send_welcome_email"
                            control={form.control}
                            render={({ field }) => (
                                <Checkbox
                                    checked={field.value}
                                    onChange={(e) =>
                                        field.onChange(e.target.checked)
                                    }
                                />
                            )}
                        />
                        <label className="text-sm font-medium cursor-pointer">
                            {tCustomer('contact.send-welcome-email')}
                        </label>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                        <ShadcnButton
                            type="button"
                            variant="outline"
                            onClick={() => {
                                onOpenChange(false)
                                form.reset()
                            }}
                        >
                            {t('cancel')}
                        </ShadcnButton>
                        <ShadcnButton type="submit" disabled={isCreating}>
                            {isCreating && (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            )}
                            {t('create')}
                        </ShadcnButton>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    )
}

export default CreateAccountDialog
